// ========================================
// PAGE AdminLogin
// Authentification Firebase
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAdminAuth from '../../hooks/useAdminAuth';
import { FiMail, FiLock, FiLogIn, FiArrowLeft } from 'react-icons/fi';
import '../../styles/admin.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login, error, user } = useAdminAuth();

  // Vérifier si l'utilisateur a passé le portail
  useEffect(() => {
    const portalAccess = sessionStorage.getItem('portalAccess');
    const portalTimestamp = sessionStorage.getItem('portalTimestamp');
    
    // Vérifier si l'accès au portail est valide (expire après 5 minutes)
    const isValidAccess = portalAccess === 'granted' && 
      portalTimestamp && 
      (Date.now() - parseInt(portalTimestamp)) < 5 * 60 * 1000;
    
    if (!isValidAccess) {
      navigate('/');
    }
  }, [navigate]);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (user) {
      sessionStorage.removeItem('portalAccess');
      sessionStorage.removeItem('portalTimestamp');
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Connexion réussie !');
      sessionStorage.removeItem('portalAccess');
      sessionStorage.removeItem('portalTimestamp');
      navigate('/admin');
    } else {
      toast.error(result.error);
    }
    
    setIsSubmitting(false);
  };

  const handleBack = () => {
    sessionStorage.removeItem('portalAccess');
    sessionStorage.removeItem('portalTimestamp');
    navigate('/');
  };

  return (
    <div className="admin-login">
      <div className="login-card">
        <button 
          className="back-button"
          onClick={handleBack}
          type="button"
        >
          <FiArrowLeft />
          Retour
        </button>

        <div className="login-header">
          <h1>Connexion</h1>
          <p>Connectez-vous à votre espace admin</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Adresse email
            </label>
            <div className="input-with-icon">
              <FiMail className="input-icon" />
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Mot de passe
            </label>
            <div className="input-with-icon">
              <FiLock className="input-icon" />
              <input
                type="password"
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                Connexion...
              </>
            ) : (
              <>
                <FiLogIn />
                Se connecter
              </>
            )}
          </button>
        </form>
      </div>

      <style>{`
        .back-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: none;
          border: none;
          color: var(--color-gray-500);
          font-size: var(--text-sm);
          cursor: pointer;
          padding: 0;
          margin-bottom: var(--spacing-6);
          transition: color var(--transition-fast);
        }
        
        .back-button:hover {
          color: var(--color-primary);
        }
        
        .login-header {
          text-align: center;
          margin-bottom: var(--spacing-8);
        }
        
        .login-header h1 {
          margin-bottom: var(--spacing-2);
        }
        
        .login-header p {
          color: var(--color-gray-500);
          margin-bottom: 0;
        }
        
        .input-with-icon {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-gray-400);
        }
        
        .input-with-icon .form-input {
          padding-left: 3rem;
        }
        
        .btn-block {
          width: 100%;
          margin-top: var(--spacing-6);
        }
        
        .btn-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-right: 0.5rem;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;